// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import { News, TitleLink, Link } from "./Item";
import { ColorfulCup, LineCup } from "../Icons/Tournaments";
import Sticker from "../Tournaments/Sticker";

import Timer, {
  getActive,
  getCompleted,
  getDuration,
  getNew
} from "../Tournaments/Timer";
import HowMuchInBucks from "../HowMuchInBucks";
import { rtl, fontSize } from "../common/styled-mixins";

import type { Tournament } from "../../redux/CommonTypes";
import { gaReducer } from "../GA";

const Container = styled(News)`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  flex-flow: row nowrap;
  font-size: ${fontSize(12, 12)};
  line-height: 1.2;
  color: #919191;
`;

const Contents = styled.div`
  flex: 1;
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-content: flex-start;
  align-items: flex-end;
  flex-grow: 0;
  flex-shrink: 0;
  /* flex-basis: 163px;*/
  max-width: 163px;
  padding-top: 15px;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  flex: 1;
  width: 100%;
`;

const IconContainer = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 72px;
  align-self: flex-start;
  width: 72px;
  ${rtl("margin-left", "auto")};
`;

const LineCupIcon = styled(LineCup)`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 20px;
  width: 20px;
  ${rtl("margin-right", "12px")};
`;

const InnerContainer = styled.div`
  margin-top: 15px;
`;

const InnerFlexContainer = styled(InnerContainer)`
  display: flex;
  flex-direction: column;
  margin-top: 15px;
`;

const PrizeFund = styled.div`
  margin-bottom: 5px;
`;

const MoneyContainer = styled.div`
  font-size: ${fontSize(14, 14)};
`;

const MoneySpan = styled.span`
  font-weight: 700;
  color: ${({ theme }) =>
    theme.mode === "light" ? theme.upper : theme.indexMajor};
`;

const Text = styled.span`
  font-weight: 700;
  color: #343434;
`;

const Time = styled(Text)`
  font-size: ${fontSize(14, 14)};
`;

const Actions = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-content: space-between;
  align-items: flex-end;
  min-height: 33px;
  margin-top: auto;
  padding-top: 15px;
  max-width: 200px;

  ${({ needInvitation }) =>
    needInvitation &&
    css`
      min-height: 37px;
      padding-top: 0;
      align-items: center;
    `};

  button {
    width: 100%;
    margin: 0;
    min-height: 37px;
  }
`;

const InvititionWrapper = styled.div``;

const LinkInvitation = styled(Link)`
  color: #3b9478;
  text-decoration: none;
`;

const InvititionText = styled.div`
  color: #3b9478;
  font-size: ${fontSize(12)};
  font-weight: 700;
  margin-bottom: 15px;
  text-decoration: none;
`;

const InvititionButton = styled.button`
  background-color: #0085eb;
  color: #fff;
  font-size: ${fontSize(12)};
  height: 37px;
  width: 100%;
  border-radius: 19px;
  border: 0;
  margin: 0;
  padding: 0 10px;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
`;

const InvititionMore = styled.div`
  display: flex;
  min-height: 37px;
  margin-top: auto;
  text-align: center;
  justify-content: center;
  align-items: center;
  min-width: 72px;
`;

type Props = {
  ...Tournament,
  time?: string,
  children: React.Node
};

function ItemTournament(props: Props) {
  const {
    title,
    id,
    participation_status,
    prize_fund,
    prize_fund_usd,
    children
  } = props;

  if (participation_status === 1) {
    return null;
  }

  // режим приглашения в турнир включаем у ограниченных турниров, в которые
  // приглашен пользователь
  const invitation = props.is_limited && props.invited;

  const needInvitation = !(
    (props.is_limited && props.limited_for_money) ||
    props.invited
  );

  const gaEvent = type => {
    return () =>
      gaReducer("BELL_GO_TO_TOURNAMENT", {
        type
      });
  };

  return (
    <Container>
      <Contents>
        <Title>
          <LineCupIcon />{" "}
          <TitleLink to={`/tournaments/${id}`} onClick={gaEvent("title")}>
            {title}
          </TitleLink>
        </Title>

        <InnerFlexContainer>
          <div>
            <Timer
              days={getDuration(props, "days")}
              hours={getDuration(props, "hours")}
              minutes={getDuration(props, "minutes")}
              seconds={getDuration(props, "seconds")}
              active={getActive(props)}
              isCompleted={getCompleted(props)}
              labelOnTop={true}
              flexDirection="column"
              alignItems="flex-start"
              noIcon
              TimeStyle={Time}
              TextStyle={Text}
            />
          </div>
        </InnerFlexContainer>
        <InnerFlexContainer>
          <PrizeFund>
            <Gettext>Prize fund</Gettext>:
          </PrizeFund>
          <MoneyContainer>
            <HowMuchInBucks
              value={prize_fund}
              valueUSD={prize_fund_usd}
              valueStyle={MoneySpan}
              symbolStyle={MoneySpan}
            />
          </MoneyContainer>
        </InnerFlexContainer>

        <InnerContainer>
          {invitation ? (
            <InvititionWrapper>
              <LinkInvitation
                to={`/tournaments/${id}`}
                onClick={() => gaReducer("BELL_GO_TO_TOURNAMENT_INVITE")}
              >
                <InvititionText>
                  <Gettext>
                    You have a special invitation for this tournament
                  </Gettext>
                </InvititionText>
                <InvititionButton>
                  <Gettext>Use the invitation</Gettext>
                </InvititionButton>
              </LinkInvitation>
            </InvititionWrapper>
          ) : (
            <Actions needInvitation={needInvitation}>{children}</Actions>
          )}
        </InnerContainer>
      </Contents>
      <Side>
        <IconContainer>
          <ColorfulCup width="72px" />
        </IconContainer>

        <InvititionMore>
          <Link to={`/tournaments/${id}`} onClick={gaEvent("details")}>
            <Gettext>Details</Gettext>
          </Link>
        </InvititionMore>
      </Side>

      <Sticker
        limited={props.is_limited}
        isNew={getNew(props)}
        active={getActive(props)}
        type={props.type}
        subtype={props.subtype}
      />
    </Container>
  );
}

export default ItemTournament;



// WEBPACK FOOTER //
// ./src/components/Bell/ItemTournament.js